import { Box } from "@mui/material";
import lottie from "lottie-web";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import logoAnimation from "../assets/animations/LogoMask.json";
import { useGlobalSnackbar } from "../context/SnackbarContext";
import { initialBenefitsFetch } from "../permissions/benefits/redux/benefits.init";
import { setCompanies, updateUser } from "../redux/features/userStateSlice";
import { registerWithServer } from "../sockets";
import { decodeToken } from "../utils";
import { useCompanies } from "./hooks/useCompanies";
import { useToken } from "./hooks/useToken";

const FORBIDDEN_PATHS_FOR_TOKEN_USER = new Set([
  "reset",
  "join",
  "signin",
  "signup",
  "activation",
  "deactivation",
  "connectacleaner",
  "jobs",
  "invitation",
  "flow-hub",
]);

const buildUserPayload = (userInfo, companies, savedCompanyId) => ({
  uid: userInfo?.uid ?? "",
  avatar: userInfo?.avatar ?? null,
  address: userInfo?.address,
  email: userInfo?.email ?? "",
  firstName: userInfo?.firstName ?? "",
  lastName: userInfo?.lastName ?? "",
  birthDate: userInfo?.birthDate ?? "",
  country: userInfo?.country ?? "",
  countryCode: userInfo?.countryCode ?? "",
  activeCompanyId: savedCompanyId ? Number(savedCompanyId) : (companies?.[0]?.companyId ?? null),
  workforces: userInfo?.workforces ?? [],
  roles: userInfo?.roles ?? [],
  hasCoverageAreas: userInfo?.coverageAreas?.length > 0,
  subscription: userInfo?.subscription?.id
    ? {
        id: userInfo?.subscription?.id,
        paypalSubscriptionId: userInfo?.subscription?.paypalId,
        planName: userInfo?.subscription?.plan?.product?.name,
        planId: userInfo?.subscription?.plan?.id,
        planPaypalId: userInfo?.subscription?.plan?.paypalPlanId,
        hexColor: userInfo?.subscription?.plan?.product?.hexColor,
        createdAt: userInfo?.subscription?.createdAt,
      }
    : null,
});

export const ValidateToken = ({ children, checkById = false, checkByParam = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [, indexPath] = pathname.split("/");
  const savedCompanyId = sessionStorage.getItem("activeCompanyId");
  const { showSnackbar } = useGlobalSnackbar();

  const [showSplash, setShowSplash] = useState(false);
  const animationContainerRef = useRef(null);

  const {
    accessToken,
    isValidToken,
    isAllowedWithoutToken,
    isLoading: tokenLoading,
    error,
  } = useToken({
    checkById,
    checkByParam,
  });

  const companiesQuery = useCompanies(!checkById && !checkByParam ? accessToken : null);

  useEffect(() => {
    if (tokenLoading) {
      setShowSplash(true);
      return;
    }

    const timeoutId = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [tokenLoading]);

  useEffect(() => {
    if (!showSplash || !animationContainerRef.current) return;

    const animation = lottie.loadAnimation({
      container: animationContainerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: logoAnimation,
    });

    return () => animation.destroy();
  }, [showSplash]);

  useEffect(() => {
    if (!error) return;

    const status = error?.response?.status;
    const msg = error?.response?.data?.message;

    if (status === 400 && msg === "companyId is required to resolve Business/Admin role") {
      showSnackbar("You do not belong to any company, you need to wait to be invited to an organization.", "error");
      return;
    }

    // if (!FORBIDDEN_PATHS_FOR_TOKEN_USER.has(currentSection)) {
    // navigate("/signin");
    // }
  }, [error, navigate, showSnackbar]);

  useEffect(() => {
    if (!isValidToken || checkById || checkByParam || !accessToken) return;
    // Esperamos la respuesta para no vaciar companies mientras cambia/refresca la compania.
    if (!companiesQuery.data) return;

    const userInfo = decodeToken(accessToken);
    if (!userInfo) return;

    // const savedCompanyId = localStorage.getItem("activeCompanyId");
    const companies = companiesQuery.data?.data ?? companiesQuery.data?.companies ?? [];

    dispatch(setCompanies(companies));
    dispatch(updateUser(buildUserPayload(userInfo, companies, savedCompanyId)));
    dispatch(initialBenefitsFetch());

    if (userInfo.uid) registerWithServer(userInfo.uid);
  }, [isValidToken, checkById, checkByParam, accessToken, companiesQuery.data, dispatch]);

  if (showSplash) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "100vh" }}>
        <Box
          sx={{
            width: "min(260px, 55vw)",
            height: "min(260px, 55vw)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "20px",
            overflow: "hidden",
          }}
        >
          <Box ref={animationContainerRef} sx={{ width: "100%", height: "100%" }} />
        </Box>
      </Box>
    );
  }

  if (isAllowedWithoutToken || isValidToken) return <>{children}</>;

  if (/^\/(business|admin)\/agenda-inteligente(?:\/|$)/.test(pathname) && !tokenLoading) {
    return (
      <Box role="alert" sx={{ maxWidth: 560, mx: "auto", p: 4, textAlign: "center" }}>
        <h1>Inicia sesión para abrir Agenda Inteligente</h1>
        <p>Esta pestaña no tiene una sesión activa o la sesión expiró. Puedes volver a la pestaña donde ya estabas trabajando.</p>
        <a href="/signin">Ir al inicio de sesión</a>
      </Box>
    );
  }

  // if (!accessToken && !FORBIDDEN_PATHS_FOR_TOKEN_USER.has(currentSection)) {
  // navigate("/signin");
  // }

  return null;
};
